import { SiteUrls } from '@tableau/shared-urls';
import { DataGrid } from '@tableau/widgets-datagrid';
import * as _ from 'underscore';

import { CommonDataGridColumns } from '../../common/react/CommonDataGridColumns';
import { IActionTarget } from '../../common/react/components/ActionsMenu';
import { DataGridCells } from '../../common/react/components/dataGrid/DataGridCells';
import { SortableHeader } from '../../common/react/components/dataGrid/SortableHeader';
import { RightAlignedCell, RightAlignedHeader } from '../../common/react/components/dataGrid/StyledCellWrapper';
import { SortTool } from '../../common/react/components/SortTool';
import { IReactTranslateService } from '../../common/react/IReactTranslateService';
import { TextileHelper } from '../../ts/helpers/TextileHelper';
import { L10n } from '../../ts/L10n/L10n';
import { ContentSortOptions } from '../../ts/models/ContentSortOptions';
import { IProjectDetailInfo, IProjectInfo } from '../../ts/models/IProjectInfo';
import { OrderKeys } from '../../ts/models/OrderKeys';
import { IActionButton } from '../../ts/services/ActionPanelService';

export module ProjectGrid {

  export type ProjectGridScope = CommonDataGridColumns.ISelectColumnScope<IProjectInfo>;

  export function columns($sanitize: ng.sanitize.ISanitizeService,
                          translate: IReactTranslateService,
                          siteUrls: SiteUrls,
                          actions: IActionButton<IProjectInfo, IProjectDetailInfo>[],
                          getActionTarget: () => IActionTarget<IProjectInfo, IProjectDetailInfo>,
                          scope: ProjectGridScope,
                          anyActionsAllowed: boolean)
    : DataGrid.Column[] {

    const nameColumn: DataGrid.Column = CommonDataGridColumns.withExtraSpace({
        name: 'name-col',
        dataKey: 'name',
        headerText: translate('column_name'),
        direction: 'asc',
        headerFormatter: SortableHeader.Formatter(),
        cellFormatter: DataGridCells.Link((row: DataGrid.Row) => {
          const titleMaxLength = 511; // IE only allows up to 512 characters in tooltips (unlike Firefox/Chrome)
          const name = row.data.name;
          const description = TextileHelper.getPlainTextFromTextile(row.data.description, $sanitize);
          const title = description ? name + '\n' + description : name;

          return {
            text: name,
            title: L10n.elideIfLong(title, titleMaxLength),
            url: siteUrls.projectUrl(row.data.id),
            iconClassName: 'tb-grid-icon-project',
            iconTitle: translate('project')
          };
        }),
      });

    const nameColumnWithActions = CommonDataGridColumns.withActions(nameColumn, {
        actions: actions,
        getActionTarget: getActionTarget,
        translate: translate
      }, scope);

    const childProjectCountColumn: DataGrid.Column = {
      name: 'child-count-col',
      dataKey: 'childProjectCount',
      headerText: translate('column_numChildren'),
      headerFormatter: RightAlignedHeader(SortableHeader.Formatter()),
      cellFormatter: RightAlignedCell(DataGridCells.LocalizedCount('childProjectCount')),
      direction: 'desc',
    };

    const workbookCountColumn: DataGrid.Column = {
      name: 'workbook-count-col',
      dataKey: 'workbookCount',
      headerText: translate('column_numWorkbooks'),
      headerFormatter: RightAlignedHeader(SortableHeader.Formatter()),
      cellFormatter: RightAlignedCell(DataGridCells.LocalizedCount('workbookCount')),
      direction: 'desc',
    };

    const sheetCountColumn: DataGrid.Column = {
      name: 'view-count-col',
      dataKey: 'viewCount',
      headerText: translate('column_numViews'),
      headerFormatter: RightAlignedHeader(SortableHeader.Formatter()),
      cellFormatter: RightAlignedCell(DataGridCells.LocalizedCount('viewCount')),
      direction: 'desc',
    };

    const datasourceCountColumn: DataGrid.Column = {
      name: 'datasource-count-col',
      dataKey: 'datasourceCount',
      headerText: translate('column_numDatasources'),
      headerFormatter: RightAlignedHeader(SortableHeader.Formatter()),
      cellFormatter: RightAlignedCell(DataGridCells.LocalizedCount('datasourceCount')),
      direction: 'desc',
    };

    const createdAtColumn: DataGrid.Column = {
      name: 'created-at-col',
      dataKey: 'createdAt',
      headerText: translate('column_created'),
      direction: 'desc',
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: DataGridCells.DateTimeCell('createdAt')
    };

    return _.compact([
      anyActionsAllowed && CommonDataGridColumns.selected(scope),
      anyActionsAllowed ? nameColumnWithActions : nameColumn,
      childProjectCountColumn,
      workbookCountColumn,
      sheetCountColumn,
      datasourceCountColumn,
      CommonDataGridColumns.owner(translate, siteUrls),
      createdAtColumn
    ]);
  }

  export function sortOptions(): SortTool.SortOption[] {
    return ContentSortOptions.sortOptionsForValidKeys([
      OrderKeys.name,
      OrderKeys.childProjectCount,
      OrderKeys.workbookCount,
      OrderKeys.viewCount,
      OrderKeys.datasourceCount,
      OrderKeys.ownerName,
      OrderKeys.createdAt
    ]);
  }
}
